import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { List, Task } from "../../types";
import { listEmojiOrDefault } from "../../constants/defaults";

function withStoredEmoji(updates: { name?: string; emoji?: string }) {
  if (updates.emoji === undefined) return updates;
  return { ...updates, emoji: listEmojiOrDefault(updates.emoji) };
}

export function useUpdateList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: { name?: string; emoji?: string };
    }) => {
      const next = withStoredEmoji(updates);
      const { error } = await supabase
        .from("lists")
        .update(next)
        .eq("id", id);
      if (error) throw error;
    },

    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.lists });
      await queryClient.cancelQueries({ queryKey: queryKeys.tasks });

      const next = withStoredEmoji(updates);
      const prevLists = queryClient.getQueryData<List[]>(queryKeys.lists);
      const prevTasks = queryClient.getQueryData<Task[]>(queryKeys.tasks);

      // Each cache is left alone when it has not loaded: writing `[]` into it
      // would flip its query out of its pending state and drop the app's
      // loading gate, and onError's snapshot guard could not put it back.
      queryClient.setQueryData<List[]>(queryKeys.lists, (old) =>
        old ? old.map((l) => (l.id === id ? { ...l, ...next } : l)) : old
      );

      // Patch the tasks cache so list display names/emojis stay current
      queryClient.setQueryData<Task[]>(queryKeys.tasks, (old) =>
        old
          ? old.map((t) =>
              t.list_id === id
                ? {
                    ...t,
                    ...(next.name !== undefined && { list: next.name }),
                    ...(next.emoji !== undefined && {
                      listEmoji: next.emoji,
                    }),
                  }
                : t
            )
          : old
      );

      return { prevLists, prevTasks };
    },

    onError: (err, _, context) => {
      if (context?.prevLists)
        queryClient.setQueryData<List[]>(queryKeys.lists, context.prevLists);
      if (context?.prevTasks)
        queryClient.setQueryData<Task[]>(queryKeys.tasks, context.prevTasks);
      Sentry.captureException(err, { extra: { mutation: "useUpdateList" } });
      toast.error("Failed to update list. Please try again.");
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.lists });
    },
  });
}
